import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { apiURI, authErrorRedirect, downloadCsv } from "..";

/**
 * Server-side pagination + per-column search for list endpoints that return
 * `{ rows, total, page, pageSize }`.
 *
 * The parent computes the endpoint (after `${apiURI}/form/`) from the user's
 * role; this hook owns the pagination and search models and re-fetches whenever
 * the page, page size, or search changes. Server-side sorting is layered on in
 * a later step.
 *
 * @param {string|undefined} endpoint  e.g. "getItemsAll" or
 *   `getItemsEmail/${email}`. When falsy, nothing is fetched.
 * @returns pagination + search state and handlers (see return statement).
 *   `rows` is `undefined` until the first response (drives the grid's loading
 *   state), then an array (possibly empty).
 */
export function useServerSidePagination(endpoint) {
  const history = useNavigate();
  const [rows, setRows] = useState();
  const [rowCount, setRowCount] = useState(0);
  const [errors, setErrors] = useState();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Per-column search: { [field]: value }. `value` is a string for text/status
  // columns and { from, to } for date columns. Empty entries are removed so the
  // query param only carries active filters.
  const [searchModel, setSearchModel] = useState({});
  const searchKey = JSON.stringify(searchModel);
  // Bumped to visually reset the header inputs (clear-all).
  const [filterResetKey, setFilterResetKey] = useState(0);
  const activeFilterCount = Object.keys(searchModel).length;

  // Applies a single column's search value, dropping it when cleared, and
  // restarts paging from the first page so results aren't shown on a stale page.
  const handleSearchChange = useCallback((field, value) => {
    setSearchModel((prev) => {
      const next = { ...prev };
      const isEmpty =
        value == null ||
        value === "" ||
        (typeof value === "object" && !value.from && !value.to);
      if (isEmpty) {
        delete next[field];
      } else {
        next[field] = value;
      }
      return next;
    });
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, []);

  // Clears every active column search at once and remounts the header inputs.
  const handleClearAllSearch = useCallback(() => {
    setSearchModel({});
    setFilterResetKey((k) => k + 1);
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, []);

  // Server-side single-column sort: [] or [{ field, sort: "asc" | "desc" }].
  const [sortModel, setSortModel] = useState([]);
  const sortKey = JSON.stringify(sortModel);

  // Cycles a column's sort: none → asc → desc → none, and restarts paging.
  const handleSortChange = useCallback((field) => {
    setSortModel((prev) => {
      const current = prev[0];
      if (!current || current.field !== field) return [{ field, sort: "asc" }];
      if (current.sort === "asc") return [{ field, sort: "desc" }];
      return [];
    });
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, []);

  const fetchPage = useCallback(async () => {
    if (!endpoint) {
      setRows([]);
      setRowCount(0);
      return;
    }
    // Undefined rows puts the grid into its loading state while the page loads.
    setRows(undefined);
    try {
      const hasSearch = Object.keys(searchModel).length > 0;
      const hasSort = sortModel.length > 0;
      const response = await axios.get(`${apiURI}/form/${endpoint}`, {
        params: {
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          // Only send the search param when there are active filters.
          ...(hasSearch ? { search: JSON.stringify(searchModel) } : {}),
          // Only send the sort param when a column sort is active.
          ...(hasSort
            ? {
                sort: JSON.stringify({
                  field: sortModel[0].field,
                  dir: sortModel[0].sort,
                }),
              }
            : {}),
        },
      });
      const { rows: pageRows = [], total = 0 } = response.data ?? {};
      setRows(pageRows);
      setRowCount(total);
    } catch (error) {
      setRows([]);
      setRowCount(0);
      setErrors(error?.response?.data);
      authErrorRedirect(error?.response?.data, history);
    }
    // searchModel / sortModel are tracked via searchKey / sortKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endpoint,
    paginationModel.page,
    paginationModel.pageSize,
    searchKey,
    sortKey,
    history,
  ]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Downloads the FULL result set for the CURRENT search + sort as a CSV,
  // reusing this hook's endpoint. Sends the same search/sort params the
  // paginated fetch builds (minus page/pageSize), plus the caller's `columns`
  // (the frontend column list — set, order, headers, formatting) so the backend
  // knows what to export. The backend streams the whole filtered/sorted set.
  // Returns the download promise so the caller can show a pending state.
  // `filename` names the saved file; `columns` comes from buildCsvColumns.
  const exportCsv = useCallback(
    ({ filename, columns } = {}) => {
      if (!endpoint) return Promise.resolve();
      const hasSearch = Object.keys(searchModel).length > 0;
      const hasSort = sortModel.length > 0;
      return downloadCsv({
        endpoint,
        params: {
          ...(hasSearch ? { search: JSON.stringify(searchModel) } : {}),
          ...(hasSort
            ? {
                sort: JSON.stringify({
                  field: sortModel[0].field,
                  dir: sortModel[0].sort,
                }),
              }
            : {}),
          ...(columns ? { columns: JSON.stringify(columns) } : {}),
        },
        filename,
      });
    },
    // searchModel / sortModel are tracked via searchKey / sortKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, searchKey, sortKey]
  );

  return {
    rows,
    rowCount,
    errors,
    paginationModel,
    setPaginationModel,
    searchModel,
    onSearchChange: handleSearchChange,
    onClearAllSearch: handleClearAllSearch,
    activeFilterCount,
    filterResetKey,
    sortModel,
    onSortChange: handleSortChange,
    refetch: fetchPage,
    exportCsv,
  };
}
